/**
 * Home visit patient list hooks (訪問予定表 患者毎).
 * Search grouped by patient + massager dropdown.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from './useClinicContext';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import {
  searchHomeVisitPatients,
  getHomeVisitPatientMassagers,
} from '@/services/main/homeVisitPatient';
import type {
  HomeVisitPatientSearchParams,
  HomeVisitPatientSearchResponse,
  HomeVisitPatientMassagersResponse,
  HomeVisitPatientMassager,
  HomeVisitPatientMassagerFilterParams,
} from '@/pages/main/homeVisitPatient/types';

function getServiceTypeFallback(): string {
  return typeof localStorage !== 'undefined'
    ? localStorage.getItem('service_type') || SERVICE_TYPE_A2
    : SERVICE_TYPE_A2;
}

function useServiceType(): string {
  const theme = useTheme();
  return theme?.serviceType === SERVICE_TYPE_R2 || theme?.serviceType === SERVICE_TYPE_A2
    ? theme.serviceType
    : getServiceTypeFallback();
}

/**
 * Search patients with visits in date range, grouped by patient.
 * Pass null params to skip the query (initial state before first search).
 */
export function useHomeVisitPatientSearch(
  params: HomeVisitPatientSearchParams | null
) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useServiceType();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = useQuery<
    HomeVisitPatientSearchResponse,
    Error
  >({
    queryKey: [
      'home-visit-patients',
      'search',
      tenant,
      clinicNumber,
      serviceType,
      params,
    ],
    queryFn: () => searchHomeVisitPatients(tenant, clinicNumber, params!, serviceType),
    enabled: !!tenant && !!clinicNumber && params !== null,
    placeholderData: prev => prev,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const responseData = data?.data;
  const rows = responseData?.data ?? [];
  const pagination = responseData?.pagination ?? null;
  const allPatientIds = responseData?.all_patient_ids ?? [];

  return { rows, pagination, allPatientIds, isLoading: isLoading || isFetching, isError, isPending, refetch, error };
}

/**
 * Get massagers for dropdown filtered by selected visit range.
 */
export function useHomeVisitPatientMassagers(
  filters?: HomeVisitPatientMassagerFilterParams | null
) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = useServiceType();
  const toast = useAppToast();

  const visitDateFrom = filters?.visit_datetime_from ?? null;
  const visitDateTo = filters?.visit_datetime_to ?? null;

  const { data, isLoading, isError, isPending, refetch, error } = useQuery<
    HomeVisitPatientMassagersResponse,
    Error
  >({
    queryKey: [
      'home-visit-patients',
      'massagers',
      tenant,
      clinicNumber,
      serviceType,
      visitDateFrom,
      visitDateTo,
    ],
    queryFn: () =>
      getHomeVisitPatientMassagers(tenant, clinicNumber, serviceType, {
        visit_datetime_from: visitDateFrom,
        visit_datetime_to: visitDateTo,
      }),
    enabled: !!tenant && !!clinicNumber,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  const massagers: HomeVisitPatientMassager[] = data?.data?.data ?? [];

  return { massagers, isLoading, isError, isPending, refetch, error };
}
